/*
 * Filapen Variant-Sync — Focal 13 Integration
 * ----------------------------------------------
 * Combines two responsibilities:
 *   1. Variant-Fetch-Rewrite: Focal's variant-picker sends ?option_values=X,
 *      but Section Rendering API only understands ?variant=ID. We patch
 *      window.fetch to translate the URL before the request goes out.
 *   2. Sternify-Bundle → Focal Variant-Picker Sync: When the user picks a
 *      variant inside a Sternify bundle widget, Focal's gallery doesn't
 *      update because Sternify doesn't click into <variant-picker>. We
 *      observe Sternify's data-variant-id and programmatically check the
 *      matching radio so Focal's <product-media> re-filters images.
 *
 * Depends on: window.__variants — an array of { id, options[] } generated
 *             inline in theme.liquid by Liquid (must exist before this
 *             script runs).
 *
 * Loaded with defer in layout/theme.liquid on product pages with >1 variant.
 * Defer guarantees execution after HTML parsing but in DOM order, so this
 * file runs BEFORE theme.js — fetch-patch is installed before any cart logic.
 */

(function () {
  /* --------------------------------------------------------------------
   * Part 1 — Variant-Fetch-Rewrite
   * Translates /products/x?option_values=a,b → /products/x?variant=12345
   * before the request is dispatched. Touches only product URLs.
   * ----------------------------------------------------------------- */
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (url.indexOf('option_values=') !== -1 && url.indexOf('/products/') !== -1) {
        var urlObj = new URL(url, window.location.origin);
        if (urlObj.searchParams.has('option_values') && !urlObj.searchParams.has('variant')) {
          var rawValues = urlObj.searchParams.get('option_values').split(',');
          var values = rawValues.map(function (s) { return s.trim(); });
          var matched = (window.__variants || []).find(function (v) {
            return values.every(function (val) { return v.options.indexOf(val) !== -1; });
          });
          if (matched) {
            urlObj.searchParams.delete('option_values');
            urlObj.searchParams.set('variant', matched.id);
            var newPath = urlObj.pathname + urlObj.search;
            if (typeof input === 'string') {
              input = newPath;
            } else {
              input = new Request(newPath, input);
            }
          }
        }
      }
    } catch (e) { /* swallow — keep original fetch behaviour on any error */ }
    return origFetch.call(this, input, init);
  };
})();

(function () {
  /* --------------------------------------------------------------------
   * Part 2 — Sternify-Bundle → Focal Variant-Picker Sync
   * ----------------------------------------------------------------- */
  if (window.__SternifyFocalSync) return;
  window.__SternifyFocalSync = true;

  /* Re-Entrancy-Guard: prevent infinite loop if Sternify reacts to our
     variant-picker click by setting data-variant-id again */
  var syncing = false;
  var lastSyncedId = null;

  function findVariantById(id) {
    var idStr = String(id).replace('gid://shopify/ProductVariant/', '').trim();
    return (window.__variants || []).find(function (v) { return String(v.id) === idStr; });
  }

  function cssEscape(s) {
    try { return CSS.escape(s); } catch (e) { return String(s).replace(/["'\\]/g, '\\$&'); }
  }

  /* Trigger Focal's variant-picker for the given variant ID.
     Strategy: programmatically click each radio matching the variant's options */
  function syncVariantPickerTo(variantId) {
    if (syncing) return false;
    var idStr = String(variantId).replace('gid://shopify/ProductVariant/', '').trim();
    if (!/^\d{6,}$/.test(idStr)) return false;
    if (idStr === lastSyncedId) return false; /* already synced */

    var variant = findVariantById(idStr);
    if (!variant) return false;

    var pickers = document.querySelectorAll('variant-picker');
    if (!pickers.length) return false;

    syncing = true;
    var anySwitched = false;
    try {
      pickers.forEach(function (picker) {
        variant.options.forEach(function (optValue, idx) {
          var pos = idx + 1;
          var sel = 'input[type="radio"][data-option-position="' + pos + '"][value="' + cssEscape(optValue) + '"]';
          var radio = picker.querySelector(sel);
          if (!radio) {
            radio = picker.querySelector('input[type="radio"][name*="option-' + pos + '"][value="' + cssEscape(optValue) + '"]');
          }
          if (!radio) {
            var radios = picker.querySelectorAll('input[type="radio"][value="' + cssEscape(optValue) + '"]');
            if (radios.length === 1) radio = radios[0];
          }
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('input', { bubbles: true }));
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            anySwitched = true;
          }
        });
      });
      if (anySwitched) lastSyncedId = idStr;
    } finally {
      /* Release sync flag after microtask so reentrant triggers in same
         tick are still blocked */
      setTimeout(function () { syncing = false; }, 0);
    }
    return anySwitched;
  }

  /* Find the Sternify bundle container — class names vary between
     Sternify versions */
  function findBundleRoot() {
    return document.querySelector(
      '[data-bundle-slot] [data-app-block], ' +
      '.st_variantbundle-app, ' +
      '[class*="st_variantbundle"], ' +
      '[class*="st_bundle"]'
    );
  }

  /* MutationObserver on data-variant-id in the bundle tree.
     Max-retries prevents endless polling on products without Sternify bundle. */
  var bundleAttempts = 0;
  var MAX_BUNDLE_ATTEMPTS = 30; /* 30 * 500ms = 15 seconds total wait */

  function setupBundleObserver() {
    var root = findBundleRoot();
    if (!root) {
      if (++bundleAttempts >= MAX_BUNDLE_ATTEMPTS) return; /* no bundle on this page — give up */
      setTimeout(setupBundleObserver, 500);
      return;
    }

    var observer = new MutationObserver(function (mutations) {
      if (syncing) return;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes' && m.attributeName === 'data-variant-id') {
          var raw = m.target.getAttribute('data-variant-id') || '';
          var id = raw.replace('gid://shopify/ProductVariant/', '').trim();
          if (/^\d{6,}$/.test(id)) {
            syncVariantPickerTo(id);
            return; /* one sync per mutation batch is enough */
          }
        }
      }
    });
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-variant-id', 'class', 'data-active'],
      subtree: true
    });

    /* Fallback: change event on dropdowns inside the bundle */
    root.addEventListener('change', function () {
      if (syncing) return;
      setTimeout(function () {
        var active = root.querySelector('.st_variantbundle-box.active, .st_bundle-box.active, [data-active="true"][data-variant-id]')
                 || root.querySelector('[data-variant-id]');
        if (active) {
          var raw = active.getAttribute('data-variant-id') || '';
          var id = raw.replace('gid://shopify/ProductVariant/', '').trim();
          if (/^\d{6,}$/.test(id)) syncVariantPickerTo(id);
        }
      }, 120);
    });
  }

  /* Wrap Sternify's setVariantId directly — robust primary source.
     Polling fallback below in case Sternify isn't loaded yet when we are. */
  function hookSternify() {
    if (!window.SternifyCartInterceptor) return false;
    if (window.SternifyCartInterceptor._focal13_sync) return true;
    var orig = window.SternifyCartInterceptor.setVariantId;
    if (typeof orig !== 'function') return false;
    window.SternifyCartInterceptor.setVariantId = function (id) {
      var result = orig.apply(this, arguments);
      if (id) setTimeout(function () { syncVariantPickerTo(id); }, 0);
      return result;
    };
    window.SternifyCartInterceptor._focal13_sync = true;
    return true;
  }

  function boot() {
    setupBundleObserver();
    if (!hookSternify()) {
      var attempts = 0;
      var poll = setInterval(function () {
        if (hookSternify() || ++attempts > 40) clearInterval(poll);
      }, 200);
    }
    window.addEventListener('SternifyCartInterceptorReady', hookSternify);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
